import React, { useEffect, useState } from "react";
import { TextField, Button, Select, MenuItem, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, List, ListItem } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FirebaseConfig } from "../../../data/firebaseConfig";
import { allRoles } from "../../login/authentication";
import { query, where, getDocs } from "firebase/firestore";
import { Person, useFirebaseService } from "../../../data/FirebaseService";
import { toast } from "react-toastify";

interface AddGroupMembersProps {
    onAddPeople: () => void;
    groupName: string;
}

export const AddGroupMembers: React.FC<AddGroupMembersProps> = ({ onAddPeople, groupName }) => {
    const [people, setPeople] = useState<Person[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const functions = getFunctions(FirebaseConfig.app);
    const addGroupMembers = httpsCallable(functions, "addGroupMembers");
    const firebaseService = useFirebaseService();

    useEffect(() => {
        if (firebaseService && groupName) {
            const fetchGroup = async () => {
                const fetchMembers = async () => {
                    const peopleRef = await firebaseService.getCollection(`people`);
                    const q = query(peopleRef,
                        where('groups', "array-contains", groupName),
                    );
                    const querySnapshot = await getDocs(q);
                    const peopleWithGroup = querySnapshot.docs.map(doc => (doc.id));

                    const allPeopleSnapshot = await getDocs(peopleRef);
                    const peopleWithoutGroup = allPeopleSnapshot.docs
                        .filter(doc => !peopleWithGroup.includes(doc.id))
                        .map(doc => ({ id: doc.id, ...doc.data() }) as Person)
                        .sort((a, b) => {
                            // First, compare by first name
                            const firstNameComparison = a.firstName.localeCompare(b.firstName);
                            if (firstNameComparison !== 0) return firstNameComparison;

                            // If first names are equal, compare by last name
                            const lastNameComparison = a.lastName.localeCompare(b.lastName);
                            if (lastNameComparison !== 0) return lastNameComparison;

                            // If both first and last names are equal, compare by email
                            return a.email.localeCompare(b.email);
                        });
                    setPeople(peopleWithoutGroup);
                };

                fetchMembers();
            };

            fetchGroup();
        }
    }, [firebaseService, groupName]);

    const handleAddPeople = async () => {
        setLoading(true);
        try {
            const result = await addGroupMembers({
                groupName: groupName,
                memberIds: selectedPeople.map((personId) => (personId)),
            });

            console.log("Firebase response:", result.data);
            // If the response is okay
            toast.success("Group members added successfully!");
            onAddPeople();
            setOpen(false);
        } catch (error) {
            // Catch and display the error message
            if (error instanceof Error) {
                setMessage("Failed to add group members.");
            } else {
                setMessage("Failed to add group members.");
            }
        }
        setLoading(false);
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setMessage("");
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, personId: string) => {
        setSelectedPeople((prevSelected) =>
            event.target.checked
                ? [...prevSelected, personId]
                : prevSelected.filter((id) => id !== personId)
        );
    };

    return (
        <div>
            <Button variant="contained" color="primary" onClick={handleClickOpen} style={{ marginBottom: 16 }}>
                Add Members
            </Button>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add People to {groupName}</DialogTitle>
                <DialogContent>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <List>
                            {people.map((person) => (
                                <ListItem key={person.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedPeople.includes(person.id)}
                                                onChange={(e) => handleCheckboxChange(e, person.id)}
                                                name={person.id}
                                            />
                                        }
                                        label={(person.firstName + " " + person.lastName).trim() + (person.email ? " (" + person.email + ")" : "")}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {message && (
                        <Typography variant="body1" color="textSecondary" style={{ marginTop: 16 }}>
                            {message}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleAddPeople} disabled={loading} color="primary">
                        Add People
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
